import type { FieldSpec, InterviewTemplate } from "../templates/types";
import type { FieldState, SafetyState } from "./types";

/** A field asked this many times without landing on `filled` is marked `unknown` and skipped thereafter. */
export const ASK_CAP = 2;

/** Hard ceiling on turns regardless of field completion — prevents an interview running forever on vague answers. */
export const TURN_CAP = 24;

/**
 * Fields whose ask count just reached ASK_CAP without a committed value
 * become `unknown`. Pure — the reducer applies the result; nothing here
 * mutates state directly (§4: the reducer is the only mutator).
 */
export function applyAskCap(
  template: InterviewTemplate,
  fields: Record<string, FieldState>,
): Record<string, FieldState> {
  const next: Record<string, FieldState> = { ...fields };

  for (const field of template.fields) {
    if (!field.askable) continue;
    const state = next[field.id];
    if (!state) continue;
    if (state.status === "filled" || state.status === "skipped" || state.status === "unknown") continue;
    if (state.askedCount >= ASK_CAP) {
      next[field.id] = { ...state, status: "unknown" };
    }
  }

  return next;
}

/**
 * Walks askable fields in template order and returns the first one that
 * still needs asking. This is the ONLY place next-field selection happens —
 * the LLM's `clarification_suggested`/hint is advisory input, never a
 * substitute (§4, §8). Returns `null` when every askable field is filled,
 * skipped, or unknown.
 */
export function nextTarget(
  template: InterviewTemplate,
  fields: Record<string, FieldState>,
): FieldSpec | null {
  for (const field of template.fields) {
    if (!field.askable) continue;
    const status = fields[field.id]?.status ?? "unset";
    if (status === "filled" || status === "skipped" || status === "unknown") continue;
    return field;
  }
  return null;
}

/**
 * Completion is reached when every required field is filled or skipped, OR
 * the turn cap is hit, OR safety has finalized (paused is terminal for the
 * session — §11). `followup_needed` is deliberately NOT computed here: the
 * client posts `field_summary` to /api/interview/followup and renders
 * Rails' verdict, full stop (§4, §8).
 */
export function isComplete(
  template: InterviewTemplate,
  fields: Record<string, FieldState>,
  turnCount: number,
  safety: SafetyState,
): boolean {
  if (safety.status === "paused") return true;
  if (turnCount >= TURN_CAP) return true;

  return template.fields
    .filter((f) => f.required)
    .every((f) => {
      const status = fields[f.id]?.status ?? "unset";
      return status === "filled" || status === "skipped";
    });
}
